import axios from "axios";
import { PaginatedResponse } from "../types/api";
import { Label } from "../types/labels";
import { API_BASE_URL_NO_API } from "../utils/apiConfig";

const API_BASE_URL = API_BASE_URL_NO_API;

export interface DocumentProduct {
    id: number;
    product_id: number;
    product_name: string;
    product_code?: string;
    quantity: number;
    unit_price: string;
    storage_location_id?: number;
    storage_location_code?: string;
    storage_location_name?: string;
    source_storage_location_id?: number;
    source_storage_location_code?: string;
    source_storage_location_name?: string;
}

export interface DocumentApiResponse {
    id: number;
    document_number: string;
    document_date: string;
    delivery_date?: string | null;
    document_type: string;
    document_type_display: string;
    document_status: string;
    document_status_display: string;
    document_amount: string;
    document_currency: string;
    document_notes?: string;
    document_created_at: string;
    document_updated_at: string;
    document_created_by: number;
    created_by_name: string;
    document_updated_by: number;
    updated_by_name: string;
    location?: number;
    location_name?: string;
    client?: number;
    client_name?: string;
    supplier?: number;
    supplier_name?: string;
    products: DocumentProduct[];
    labels?: Label[];
    source_document?: number;
    source_document_number?: string;
    allowed_transitions?: string[];
}

export type PaginatedDocumentsResponse = PaginatedResponse<DocumentApiResponse>;

export interface GetDocumentsParams {
    page?: number;
    page_size?: number;
    search?: string;
    type?: string;
    status?: string;
    location?: number;
    warehouse?: number;
}

export type DocumentProductRequest =
    | { product_id: number; quantity: number; unit_price: number | string; storage_location_id?: number; source_storage_location_id?: number; }
    | { product: { name: string; description?: string; category: string[]; price: number | string; supplier_ids?: number[]; label_ids?: number[]; }; quantity: number; unit_price: number | string; storage_location_id?: number; source_storage_location_id?: number; };

export interface CreateDocumentRequest {
    document_type: string;
    document_date?: string;
    delivery_date?: string | null;
    document_number?: string;
    location_id?: number;
    client_id?: number;
    supplier_id?: number;
    document_notes?: string;
    document_currency?: string;
    products?: DocumentProductRequest[];
    label_ids?: number[];
}

export interface UpdateDocumentRequest {
    document_date?: string;
    delivery_date?: string | null;
    location_id?: number;
    client_id?: number;
    supplier_id?: number;
    document_notes?: string;
    products?: DocumentProductRequest[] | null;
    label_ids?: number[] | null;
}

export interface ChangeStatusRequest {
    status: string;
}

export interface DocumentTypeOption {
    value: string;
    label: string;
}

export interface DocumentStatusOption {
    value: string;
    label: string;
}

export interface PZReceivedProduct {
    product_id: number;
    received_quantity: number;
    storage_location_id?: number;
}

export interface CreatePZFromARequest {
    products: PZReceivedProduct[];
}

export interface CreatePZFromAResponse {
    pz: DocumentApiResponse;
    kor: DocumentApiResponse | null;
}

export interface PzWzDocRef {
    line_id: number;
    document_id: number;
    document_number: string;
    document_type: string;
    document_type_display: string;
    document_date: string;
    document_status: string;
    product_id: number;
    product_name: string;
    product_code: string;
    storage_location_code: string | null;
    quantity?: number;
    allocation_id?: number;
}

export interface PzWzReceipt extends PzWzDocRef {
    received_quantity: number;
    issued_quantity: number;
    remaining_quantity: number;
    outgoing: PzWzDocRef[];
}

export interface PzWzOutgoingLine extends PzWzDocRef {
    quantity: number;
    allocated_quantity: number;
    unallocated_quantity: number;
    sources: PzWzDocRef[];
}

export interface DocumentPzWzAllocationsResponse {
    view: "source" | "outgoing" | "mixed" | "none";
    receipts: PzWzReceipt[];
    outgoing_lines: PzWzOutgoingLine[];
}

const getBaseUrl = () => {
    return API_BASE_URL.endsWith('/api') ? API_BASE_URL.slice(0, -4) : API_BASE_URL;
};

async function parseErrorBlob(blob: Blob): Promise<string> {
    try {
        const text = await blob.text();
        const j = JSON.parse(text) as { detail?: string; error?: string; message?: string };
        return j.detail || j.error || j.message || text || 'Błąd serwera';
    } catch {
        return 'Błąd serwera';
    }
}

export const documentsApi = {
    getDocuments: async (params?: GetDocumentsParams): Promise<PaginatedDocumentsResponse> => {
        const queryParams = new URLSearchParams();
        if (params?.page) {
            queryParams.append("page", params.page.toString());
        }
        if (params?.page_size) {
            queryParams.append("page_size", params.page_size.toString());
        }
        if (params?.search) {
            queryParams.append("search", params.search);
        }
        if (params?.type) {
            queryParams.append("type", params.type);
        }
        if (params?.status) {
            queryParams.append("status", params.status);
        }
        if (params?.location) {
            queryParams.append("location", params.location.toString());
        }
        if (params?.warehouse !== undefined) {
            queryParams.append("warehouse", params.warehouse.toString());
        }

        const queryString = queryParams.toString();
        const baseUrl = getBaseUrl();
        const url = `${baseUrl}/api/documents/${queryString ? `?${queryString}` : ""}`;

        const response = await axios.get<PaginatedDocumentsResponse>(url);
        return response.data;
    },

    getDocument: async (id: number): Promise<DocumentApiResponse> => {
        const baseUrl = getBaseUrl();
        const url = `${baseUrl}/api/documents/${id}/`;
        const response = await axios.get<DocumentApiResponse>(url);
        return response.data;
    },

    createDocument: async (data: CreateDocumentRequest): Promise<DocumentApiResponse> => {
        const baseUrl = getBaseUrl();
        const url = `${baseUrl}/api/documents/`;
        const response = await axios.post<DocumentApiResponse>(url, data);
        return response.data;
    },

    updateDocument: async (id: number, data: UpdateDocumentRequest): Promise<DocumentApiResponse> => {
        const baseUrl = getBaseUrl();
        const url = `${baseUrl}/api/documents/${id}/`;
        const response = await axios.patch<DocumentApiResponse>(url, data);
        return response.data;
    },

    deleteDocument: async (id: number): Promise<void> => {
        const baseUrl = getBaseUrl();
        const url = `${baseUrl}/api/documents/${id}/`;
        await axios.delete(url);
    },

    changeStatus: async (id: number, status: string): Promise<DocumentApiResponse> => {
        const baseUrl = getBaseUrl();
        const url = `${baseUrl}/api/documents/${id}/change_status/`;
        const response = await axios.post<DocumentApiResponse>(url, { status });
        return response.data;
    },

    /**
     * Generate and return KT (shipping list) PDF blob URL.
     * Caller should open it in a new tab (and revoke URL later).
     */
    printKT: async (documentId: number): Promise<string> => {
        const baseUrl = getBaseUrl();
        const url = `${baseUrl}/api/documents/${documentId}/print_kt/`;

        const response = await axios.get(url, {
            responseType: "blob",
            withCredentials: true,
        });

        const blob: Blob = response.data;
        const ct = String(response.headers["content-type"] || "").toLowerCase();

        if (ct.includes("application/json")) {
            throw new Error(await parseErrorBlob(blob));
        }

        const buf = new Uint8Array(await blob.slice(0, 5).arrayBuffer());
        const isPdf =
            buf.length >= 4 && buf[0] === 0x25 && buf[1] === 0x50 && buf[2] === 0x44 && buf[3] === 0x46; // %PDF

        if (!isPdf) {
            throw new Error("Serwer nie zwrócił poprawnego pliku PDF");
        }

        const pdfBlob =
            blob.type === "application/pdf" ? blob : new Blob([blob], { type: "application/pdf" });
        return URL.createObjectURL(pdfBlob);
    },

    createPZFromAwizacja: async (awizacjaId: number, data: CreatePZFromARequest): Promise<CreatePZFromAResponse> => {
        const baseUrl = getBaseUrl();
        const url = `${baseUrl}/api/documents/${awizacjaId}/create_pz/`;
        const response = await axios.post<CreatePZFromAResponse>(url, data);
        return response.data;
    },

    getDocumentTypes: async (): Promise<DocumentTypeOption[]> => {
        const baseUrl = getBaseUrl();
        const url = `${baseUrl}/api/documents/types/`;
        const response = await axios.get<DocumentTypeOption[]>(url);
        return response.data;
    },

    getDocumentStatuses: async (documentType?: string): Promise<DocumentStatusOption[]> => {
        const baseUrl = getBaseUrl();
        let url = `${baseUrl}/api/documents/statuses/`;
        if (documentType) {
            url += `?document_type=${documentType}`;
        }
        const response = await axios.get<DocumentStatusOption[]>(url);
        return response.data;
    },

    generateDocumentNumber: async (documentType: string, locationId: number): Promise<string> => {
        const baseUrl = getBaseUrl();
        const url = `${baseUrl}/api/documents/generate_number/`;
        const response = await axios.get<{ document_number: string; document_type: string; location_id: number }>(url, {
            params: {
                document_type: documentType,
                location_id: locationId,
            },
        });
        return response.data.document_number;
    },

    getDashboard: async (warehouseId: number): Promise<DashboardData> => {
        const baseUrl = getBaseUrl();
        const url = `${baseUrl}/api/documents/dashboard/`;
        const response = await axios.get<DashboardData>(url, {
            params: { warehouse: warehouseId },
        });
        return response.data;
    },

    getPzWzAllocations: async (id: number): Promise<DocumentPzWzAllocationsResponse> => {
        const baseUrl = getBaseUrl();
        const url = `${baseUrl}/api/documents/${id}/pz-wz-allocations/`;
        const response = await axios.get<DocumentPzWzAllocationsResponse>(url);
        return response.data;
    },
};

export interface DashboardAwizacja {
    id: number;
    document_number: string;
    document_type?: string;
    supplier_name: string | null;
    delivery_date: string | null;
    document_date: string;
    delivery_status: 'zaległa' | 'dzisiaj' | 'nadchodząca' | 'brak daty';
    location_name: string | null;
    products_count: number;
}

export interface DashboardReport {
    id: number;
    name: string;
    report_type: string;
    report_type_display: string;
    fields_count: number;
    updated_at: string;
}

export interface DashboardStats {
    awizacje_oczekujace: number;
    awizacje_dzisiaj: number;
    awizacje_zaległe: number;
    wz_w_realizacji: number;
    dokumenty_dzisiaj: number;
}

export interface DashboardData {
    awizacje: DashboardAwizacja[];
    starred_reports: DashboardReport[];
    stats: DashboardStats;
}
